<!--
// app/components/pages/mercato/simulator/MercatoTeamCard.vue
// @component MercatoTeamCard - Displays team logo, name, player count for selection
-->
<script setup lang="ts">
import type { MercatoTeam } from "~~/src/mercato/domain/models/MercatoTeam";
import { setAliasUrl } from "~~/src/Shared/utils";

type Props = {
  team: MercatoTeam;
  playerCount: number;
  isSelected: boolean;
};

const props = defineProps<Props>();

const emit = defineEmits<{
  select: [team: MercatoTeam];
}>();

const { $t } = useI18n();

const handleSelect = () => {
  emit("select", props.team);
};
</script>

<template>
  <div
    :class="['mercato-team-card', { 'mercato-team-card--selected': isSelected }]"
    @click="handleSelect"
    role="button"
    tabindex="0"
    :aria-label="$t('mercatoSimulator.step1.selectTeam', { team: team.completeName })"
    @keydown.enter="handleSelect"
    @keydown.space.prevent="handleSelect"
  >
    <!-- Team Logo and Name -->
    <div class="mercato-team-card__info">
      <NuxtImg
        :src="setAliasUrl(team.logo.url)"
        :alt="team.completeName"
        width="60"
        height="60"
        sizes="60px"
        class="mercato-team-card__logo"
      />

      <div class="mercato-team-card__header">
        <p class="mercato-team-card__name">{{ team.completeName }}</p>
        <p class="mercato-team-card__players-count">{{ playerCount }} {{ $t("menuItem.players") }}</p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.mercato-team-card {
  @apply bg-gray-800 rounded-lg p-4 border border-gray-700 transition-all duration-300 ease-in-out cursor-pointer;
}

.mercato-team-card:hover {
  @apply border-mercato;
  box-shadow:
    0 0 20px rgba(0, 201, 74, 0.3),
    0 4px 12px rgba(0, 0, 0, 0.2);
  background: linear-gradient(to bottom, rgba(0, 201, 74, 0.1), transparent);
}

.mercato-team-card:focus {
  @apply outline-none ring-2 ring-mercato ring-offset-2 ring-offset-black;
}

.mercato-team-card:focus-visible {
  @apply outline-none ring-2 ring-mercato ring-offset-2 ring-offset-black;
}

.mercato-team-card--selected {
  @apply border-mercato;
  box-shadow:
    0 0 20px rgba(0, 201, 74, 0.5),
    0 4px 12px rgba(0, 0, 0, 0.3);
  background: linear-gradient(to bottom, rgba(0, 201, 74, 0.15), transparent);
}

.mercato-team-card__info {
  @apply flex items-center justify-start gap-3 w-full;
}

.mercato-team-card__logo {
  @apply rounded-lg transition-all duration-300 ease-in-out;
}

.mercato-team-card:hover .mercato-team-card__logo,
.mercato-team-card:hover .mercato-team-card__name {
  @apply scale-110 origin-bottom-left;
}

.mercato-team-card__header {
  @apply flex-1 min-w-0;
}

.mercato-team-card__name {
  @apply text-lg font-bold transition-all duration-300 ease-in-out truncate;
}

.mercato-team-card__players-count {
  @apply text-sm text-gray-400 lowercase mt-1;
}
</style>
